import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { authenticateUser, updatePassword } from "../../dbApiFunctions";
import { isEmail } from "validator";
import "./loginStyle.css";
import { hashString } from "../../utils/utilities";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import ForgotPassword from "./ForgotPassword";
import { showSuccessAlert, showErrorAlert, showConfirmDialog } from "../../utils/sweetAlertConfig";
import "../../styles/sweetAlert.css";

const Login = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [successful, setSuccessful] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [accountLocked, setAccountLocked] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const MAX_ATTEMPTS = 5;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccessful(false);

    if (accountLocked) {
      showErrorAlert(
        'Account Locked',
        'Account is temporarily locked due to too many failed attempts.',
        'Please try again later or contact support'
      );
      return;
    }

    setIsLoggingIn(true);

    if (!validateForm()) {
      setIsLoggingIn(false);
      return;
    }

    try {
      // Authenticate with Firebase
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // If Firebase authentication is successful, proceed with user data setup
      const res = await updatePassword(formData.email, formData.password);
      
      // Reset login attempts on successful login
      setLoginAttempts(0);
      const hashedEmailValue = await hashString(formData.email);
      sessionStorage.setItem("username", hashedEmailValue);
      sessionStorage.setItem("email", formData.email);
      sessionStorage.setItem("password", formData.password);
      setIsLoggedIn(true);
      
      showSuccessAlert(
        'Login Successful!',
        'Welcome back!',
        'Redirecting to your dashboard...'
      );

      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (firebaseError) {
      console.error("Firebase authentication error:", firebaseError);
      
      // Increment login attempts on failed authentication
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      if (newAttempts >= MAX_ATTEMPTS) {
        setAccountLocked(true);
        showErrorAlert(
          'Account Locked',
          'Maximum login attempts reached (5).',
          'Account is temporarily locked. Please try again later.'
        );
      } else {
        // Customize error message based on Firebase error code
        let errorMessage = "Invalid email or password";
        let errorTitle = "Login Failed";
        let errorDetails = "Please check your credentials and try again.";

        switch (firebaseError.code) {
          case 'auth/invalid-credential':
            errorMessage = "The email or password you entered is incorrect";
            errorTitle = "Invalid Credentials";
            errorDetails = "Please check your email and password.";
            break;
          case 'auth/user-not-found':
            errorMessage = "No account found with this email address";
            errorTitle = "Account Not Found";
            errorDetails = "Please check your email or create a new account.";
            break;
          case 'auth/wrong-password':
            errorMessage = "Incorrect password. Please try again";
            errorTitle = "Wrong Password";
            errorDetails = "Please check your password and try again.";
            break;
          case 'auth/too-many-requests':
            errorMessage = "Too many failed attempts. Please try again later";
            errorTitle = "Too Many Attempts";
            errorDetails = "Please wait a moment before trying again.";
            break;
          case 'auth/user-disabled':
            errorMessage = "This account has been disabled";
            errorTitle = "Account Disabled";
            errorDetails = "Please contact support for assistance.";
            break;
          case 'auth/network-request-failed':
            errorMessage = "Network error. Please check your connection";
            errorTitle = "Network Error";
            errorDetails = "Please ensure you have a stable internet connection.";
            break;
          default:
            errorMessage = "Login failed. Please try again";
            errorTitle = "Login Error";
            errorDetails = "An unexpected error occurred. Please try again.";
        }

        showErrorAlert(
          errorTitle,
          errorMessage,
          errorDetails
        );
      }

      // Clear password field on failed attempt
      setFormData(prev => ({ ...prev, password: "" }));
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="col-md-12 login-container">
      {showForgotPassword ? (
        <ForgotPassword onBack={() => setShowForgotPassword(false)} />
      ) : (
        <div className="card card-container light-theme">
          <h3 className="login-title">Sign In</h3>

          <form onSubmit={handleLogin} className="auth-form">
            {!successful && (
              <div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    className={`form-control light-input ${errors.email ? 'is-invalid' : ''}`}
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    disabled={accountLocked || isLoggingIn}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    className={`form-control light-input ${errors.password ? 'is-invalid' : ''}`}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    disabled={accountLocked || isLoggingIn}
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>

                <div className="form-group">
                  <button
                    type="submit"
                    className="btn gradient-btn"
                    disabled={accountLocked || isLoggingIn}
                  >
                    {isLoggingIn ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </div>

                <div className="form-group mt-3">
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => setShowForgotPassword(true)}
                    disabled={accountLocked || isLoggingIn}
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>
            )}

            {loginAttempts > 0 && !accountLocked && (
              <div className="text-center mt-2">
                <small className="text-warning">
                  {MAX_ATTEMPTS - loginAttempts} attempt{MAX_ATTEMPTS - loginAttempts !== 1 ? 's' : ''} remaining
                </small>
              </div>
            )}

            {/* he use nhi kel tari chalte */}
            {message && (
              <div className={`alert ${successful ? 'alert-success' : 'alert-danger'}`} role="alert">
                {message}
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default Login;
